import { RadioGroup } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import type { VoidFunctionComponent } from 'react';

import { OIDCProviderScope } from '../../../../../shared/model-typings/oidcprovider';
import { MAX_AUDIENCES } from '../../../../../shared/model-typings/oidctrustrelationship';
import { errorMessagesListFromResponse } from '../../../../helpers/errors';
import useAvailableServiceAccounts from '../../../../hooks/api/oidc/useAvailableServiceAccounts';
import useCreateTrustRelationship from '../../../../hooks/api/oidc/useCreateTrustRelationship';
import useOIDCProvider from '../../../../hooks/api/oidc/useOIDCProvider';
import useTeams from '../../../../hooks/api/teams/useTeams';
import { Action, ResourceType, useHasPermission } from '../../../../shared/permissions';
import { ErrorAlert, icons } from '../../../generic';
import Button, { ButtonGroup, LoaderButton } from '../../../generic/Button';
import IconButton from '../../../generic/Button/IconButton';
import Checkbox from '../../../generic/Forms/Checkbox';
import FormLabel from '../../../generic/Forms/FormLabel';
import LabeledRadio from '../../../generic/Forms/LabeledRadio';
import TextInput from '../../../generic/Forms/TextInput';
import { Select } from '../../../generic/Select';
import type { SelectOption } from '../../../../types';

import styles from './ModalCreateTrustRelationship.module.less';

type ModalCreateTrustRelationshipProps = {
  isOpen: boolean;
  onHide: () => void;
  providerId: number;
  currentTeamId?: number;
};

type RequiredClaim = {
  claim: string;
  value: string;
  hasWildcards: boolean;
  id: string; // for React key
};

const ModalCreateTrustRelationship: VoidFunctionComponent<ModalCreateTrustRelationshipProps> = ({
  isOpen,
  onHide,
  providerId,
  currentTeamId,
}) => {
  const createTrustRelationshipMutation = useCreateTrustRelationship();
  const hasPermission = useHasPermission();
  const canManageOrgTrustRelationships = hasPermission({
    action: Action.Manage,
    resourceType: ResourceType.OrganizationServiceAccountTrustRelationship,
  });

  // Fetch provider to determine its scope
  const { data: provider, isLoading: isLoadingProvider } = useOIDCProvider(providerId, isOpen);

  // State management
  const [selectedServiceAccount, setSelectedServiceAccount] = useState<SelectOption<number> | null>(null);
  const [selectedScope, setSelectedScope] = useState<OIDCProviderScope>(
    canManageOrgTrustRelationships && !currentTeamId ? OIDCProviderScope.ORG : OIDCProviderScope.TEAM,
  );
  const [selectedTeam, setSelectedTeam] = useState<SelectOption<number> | null>(null);
  const [audiences, setAudiences] = useState<Array<{ value: string; id: string }>>([{ value: '', id: 'aud-0' }]);
  const [audienceErrors, setAudienceErrors] = useState<Array<string | null>>([null]);
  const [requiredClaims, setRequiredClaims] = useState<RequiredClaim[]>([
    { claim: 'sub', value: '', hasWildcards: false, id: 'claim-0' },
  ]);
  const [claimErrors, setClaimErrors] = useState<Array<{ claim: string | null; value: string | null }>>([
    { claim: null, value: null },
  ]);

  const serviceAccountTeamId = useMemo(() => {
    if (currentTeamId) {
      return currentTeamId;
    }
    if (provider?.scope === OIDCProviderScope.TEAM) {
      return provider?.scopeId;
    }
    if (selectedTeam) {
      return selectedTeam.value;
    }
    return undefined;
  }, [currentTeamId, provider?.scope, provider?.scopeId, selectedTeam]);

  // Fetch available service accounts
  // We can only fetch service accounts if we have a provider
  // If the user can not manage org trust relationships, then we need to have a selected team too
  const shouldFetchServiceAccounts = !!(provider && (canManageOrgTrustRelationships || serviceAccountTeamId));
  const {
    data: serviceAccountsData,
    fetchNextPage,
    hasNextPage,
    isLoading: isLoadingServiceAccounts,
    isFetchingNextPage,
  } = useAvailableServiceAccounts(
    {
      providerId,
      teamId: serviceAccountTeamId,
    },
    50,
    shouldFetchServiceAccounts,
  );

  // Auto-pagination
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && !isLoadingServiceAccounts && !isLoadingProvider) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, isLoadingServiceAccounts, isLoadingProvider, fetchNextPage]);

  // Flatten service accounts from all pages
  const serviceAccountOptions: Array<SelectOption<number>> = useMemo(() => {
    if (!serviceAccountsData?.pages) {
      return [];
    }
    return serviceAccountsData.pages.flatMap((page) =>
      page.results.map((account) => ({
        value: account.id,
        label: account.username || account.email || account.id.toString(),
      })),
    );
  }, [serviceAccountsData]);

  const { data: teams, isLoading: isLoadingTeams } = useTeams(isOpen);

  // Convert teams to select options
  const teamOptions: Array<SelectOption<number>> = useMemo(
    () =>
      (teams || [])
        .filter((team) => {
          return canManageOrgTrustRelationships || hasPermission({
            action: Action.Manage,
            resourceType: ResourceType.TeamServiceAccountTrustRelationship,
            teamId: team.id,
          });
        })
        .map((team) => ({
          value: team.id,
          label: team.name,
        })),
    [teams, canManageOrgTrustRelationships, hasPermission],
  );

  // Determine if scope selection should be shown
  // If the provider is team-scoped, then the trust relationship must be scoped to that team.
  // If the provider is org-scoped, then we want to show the selector if the user has permission to manage trust
  // relationships at the organization level, or if the user does not have that permission then they must be able to
  // select a team so that we can determine which service accounts are available. If we're in a context where we're
  // editing a team, then we'll use the currentTeamId and hide the selector since it does not make sense to create a
  // trust relationship in a different team from the one we're viewing.
  const shouldShowScopeSelection = provider?.scope === OIDCProviderScope.ORG && (
    canManageOrgTrustRelationships || !currentTeamId
  );

  const shouldShowTeamDropdown = shouldShowScopeSelection && selectedScope === OIDCProviderScope.TEAM && !currentTeamId;

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedServiceAccount(null);
      setSelectedScope(canManageOrgTrustRelationships ? OIDCProviderScope.ORG : OIDCProviderScope.TEAM);
      setSelectedTeam(null);
      setAudiences([{ value: '', id: 'aud-0' }]);
      setAudienceErrors([null]);
      setRequiredClaims([{ claim: 'sub', value: '', hasWildcards: false, id: 'claim-0' }]);
      setClaimErrors([{ claim: null, value: null }]);
    }
  }, [isOpen, canManageOrgTrustRelationships]);

  // Validation functions
  const validateAudience = (value: string): string | null => {
    if (!value.trim()) {
      return 'Audience is required';
    }
    return null;
  };

  const validateClaimName = (value: string, index: number): string | null => {
    if (index === 0) {
      return null;
    } // 'sub' is pre-filled and valid
    if (!value.trim()) {
      return 'Claim name is required';
    }
    return null;
  };

  const validateClaimValue = (value: string): string | null => {
    if (!value.trim()) {
      return 'Claim value is required';
    }
    return null;
  };

  const handleAudienceChange = (index: number, value: string) => {
    const newAudiences = [...audiences];
    if (!newAudiences[index]) {
      return;
    }
    newAudiences[index] = { value, id: newAudiences[index].id };
    setAudiences(newAudiences);
  };

  const handleAudienceBlur = (index: number) => {
    const error = validateAudience(audiences[index]?.value || '');
    const newErrors = [...audienceErrors];
    newErrors[index] = error;
    setAudienceErrors(newErrors);
  };

  const handleAddAudience = () => {
    if (audiences.length < MAX_AUDIENCES) {
      setAudiences(originalAudiences => {
        return [...originalAudiences, { value: '', id: `aud-${Date.now()}` }];
      });
      setAudienceErrors([...audienceErrors, null]);
    }
  };

  const handleRemoveAudience = (index: number) => {
    if (audiences.length > 1) {
      setAudiences(audiences.filter((_, i) => i !== index));
      setAudienceErrors(audienceErrors.filter((_, i) => i !== index));
    }
  };

  const handleClaimChange = (index: number, field: keyof RequiredClaim, value: string | boolean) => {
    const newClaims = [...requiredClaims];
    if (!newClaims[index]) {
      return;
    }
    newClaims[index] = { ...newClaims[index], [field]: value };
    setRequiredClaims(newClaims);
  };

  const handleClaimBlur = (index: number, field: 'claim' | 'value') => {
    const claim = requiredClaims[index];
    if (!claim) {
      return;
    }
    const newErrors = [...claimErrors];
    if (!newErrors[index]) {
      return;
    }

    if (field === 'claim') {
      newErrors[index] = { ...newErrors[index], claim: validateClaimName(claim.claim, index) };
    } else {
      newErrors[index] = { ...newErrors[index], value: validateClaimValue(claim.value) };
    }
    setClaimErrors(newErrors);
  };

  const handleAddClaim = () => {
    if (requiredClaims.length < 5) {
      setRequiredClaims([
        ...requiredClaims,
        { claim: '', value: '', hasWildcards: false, id: `claim-${Date.now()}` },
      ]);
      setClaimErrors([...claimErrors, { claim: null, value: null }]);
    }
  };

  const handleRemoveClaim = (index: number) => {
    // Don't allow removing the 'sub' claim (first one)
    if (index === 0 || requiredClaims.length <= 1) {
      return;
    }

    setRequiredClaims(requiredClaims.filter((_, i) => i !== index));
    setClaimErrors(claimErrors.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    // Validate all fields
    let hasErrors = false;

    // Validate audiences
    const newAudienceErrors = audiences.map((aud) => validateAudience(aud.value));
    setAudienceErrors(newAudienceErrors);
    if (newAudienceErrors.some((err) => err !== null)) {
      hasErrors = true;
    }

    // Validate claims
    const newClaimErrors = requiredClaims.map((claim, index) => ({
      claim: validateClaimName(claim.claim, index),
      value: validateClaimValue(claim.value),
    }));
    setClaimErrors(newClaimErrors);
    if (newClaimErrors.some((err) => err.claim !== null || err.value !== null)) {
      hasErrors = true;
    }

    // Validate service account selection
    if (!selectedServiceAccount) {
      hasErrors = true;
    }

    // Validate team selection if needed
    if (shouldShowTeamDropdown && !selectedTeam) {
      hasErrors = true;
    }

    let scope = selectedScope;
    if (!canManageOrgTrustRelationships || provider?.scope === OIDCProviderScope.TEAM) {
      scope = OIDCProviderScope.TEAM;
    }

    let scopeId: number | undefined;
    if (provider?.scope === OIDCProviderScope.TEAM) {
      scopeId = provider.scopeId;
    } else if (currentTeamId) {
      scopeId = currentTeamId;
    } else if (selectedScope === OIDCProviderScope.TEAM && selectedTeam) {
      scopeId = selectedTeam.value;
    }
    if (selectedScope === OIDCProviderScope.TEAM && !scopeId) {
      return;
    }

    if (hasErrors || !selectedServiceAccount || !provider) {
      return;
    }

    // Prepare request
    const baseRequest = {
      userId: selectedServiceAccount.value,
      providerId,
      audiences: audiences.map((aud) => aud.value).filter((val) => val.trim()),
      requiredClaims,
    };

    const request =
      scope === OIDCProviderScope.TEAM
        // We've checked above that scopeId is not undefined when the selected scope is team
        ? { ...baseRequest, scope, scopeId: scopeId! }
        : { ...baseRequest, scope };

    try {
      await createTrustRelationshipMutation.mutateAsync(request);
      onHide();
    } catch (err) {
      // Error is captured in mutation state and displayed below
    }
  };

  // Check if submit should be disabled
  const isSubmitDisabled =
    createTrustRelationshipMutation.isPending ||
    !selectedServiceAccount ||
    audiences.some((aud, i) => !aud.value.trim() || audienceErrors[i] !== null) ||
    requiredClaims.some(
      (claim, i) =>
        !claim.claim.trim() || !claim.value.trim() || claimErrors[i]?.claim !== null || claimErrors[i]?.value !== null,
    ) ||
    (shouldShowTeamDropdown && !selectedTeam);

  const apiErrors = createTrustRelationshipMutation.isError
    ? errorMessagesListFromResponse(createTrustRelationshipMutation.error)
    : null;

  return (
    <Modal show={isOpen} onHide={onHide} bsSize="lg">
      <Modal.Header closeButton>
        <Modal.Title>Add a Trust Relationship</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {apiErrors && <ErrorAlert status={{ errors: apiErrors }} />}

        {/* Scope Selection (Conditional) */}
        {shouldShowScopeSelection && (
          <>
            {canManageOrgTrustRelationships && (
              <FormLabel variant="legacy" label="Scope">
                <div className={styles.helpText}>
                  The scope is used to determine which users within FOSSA are able to modify this trust relationship.
                  If the scope is set to &quot;Organization&quot; then only users with the organization-level permission
                  will be able to modify it. When the &quot;Team&quot; scope is selected, then team admins will be able
                  to modify it as well.
                </div>

                <RadioGroup
                  value={selectedScope}
                  onChange={(e) => setSelectedScope(e.target.value as OIDCProviderScope)}
                >
                  <LabeledRadio
                    label="Organization"
                    value={OIDCProviderScope.ORG}
                    isDisabled={createTrustRelationshipMutation.isPending}
                  />
                  <LabeledRadio
                    label="Team"
                    value={OIDCProviderScope.TEAM}
                    isDisabled={createTrustRelationshipMutation.isPending}
                  />
                </RadioGroup>
              </FormLabel>
            )}

            {shouldShowTeamDropdown && (
              <FormLabel variant="legacy" label="Team" requiredText="(Required)">
                <Select
                  id="select-trust-relationship-team"
                  value={selectedTeam}
                  onChange={(option) => {
                    setSelectedTeam(option);
                    setSelectedServiceAccount(null);
                  }}
                  options={teamOptions}
                  placeholder="Choose a team..."
                  isLoading={isLoadingTeams}
                  isDisabled={createTrustRelationshipMutation.isPending}
                  isClearable={false}
                />
              </FormLabel>
            )}
          </>
        )}

        {/* Service Account Selection */}
        <FormLabel variant="legacy" label="Service Account" requiredText="(Required)">
          <Select
            id="select-trust-relationship-service-account"
            value={selectedServiceAccount}
            onChange={(option) => setSelectedServiceAccount(option)}
            options={serviceAccountOptions}
            placeholder="Choose a service account..."
            isLoading={isLoadingServiceAccounts}
            isDisabled={createTrustRelationshipMutation.isPending}
            isClearable={false}
          />
        </FormLabel>

        {/* Audiences Section */}
        <div className={styles.section}>
          <FormLabel variant="legacy" label="Audiences" requiredText="(Required)">
            <div className={styles.helpText}>
              Audience values that are valid for this trust relationship. At least one is required, up to five allowed.
            </div>
          </FormLabel>
          {audiences.map((audience, index) => (
            <div key={audience.id} className={styles.audienceRow}>
              <div className={styles.audienceInput}>
                <TextInput
                  label={`Audience ${index + 1}`}
                  isRequired
                  type="text"
                  placeholder="https://app.fossa.com"
                  value={audience.value}
                  onChange={(value) => handleAudienceChange(index, value)}
                  onBlur={() => handleAudienceBlur(index)}
                  isDisabled={createTrustRelationshipMutation.isPending}
                />
                {audienceErrors[index] && <div className={styles.error}>{audienceErrors[index]}</div>}
              </div>
              {audiences.length > 1 && (
                <IconButton
                  id={`remove-audience-${index}-icon`}
                  icon={icons.times}
                  onClick={() => handleRemoveAudience(index)}
                  isDisabled={createTrustRelationshipMutation.isPending}
                  tooltip="Remove audience"
                  testId={`remove-audience-${index}`}
                />
              )}
            </div>
          ))}
          {audiences.length < MAX_AUDIENCES && (
            <Button onClick={handleAddAudience} isDisabled={createTrustRelationshipMutation.isPending} type="secondary">
              Add Audience
            </Button>
          )}
        </div>

        {/* Required Claims Section */}
        <div className={styles.section}>
          <FormLabel variant="legacy" label="Required Claims" requiredText="(Required)">
            <div className={styles.helpText}>
              Specify claims that must be match in order to be considered trustworthy. At least one claim with name
              &ldquo;sub&rdquo; is required.
              <br />
              <strong>Wildcard syntax</strong> (when &ldquo;Has Wildcards&rdquo; is checked):
              <ul className={styles.helpList}>
                <li>
                  <code>*</code> matches zero or more characters
                </li>
                <li>
                  <code>?</code> matches exactly one character
                </li>
                <li>
                  <code>\</code> escapes the following character
                </li>
              </ul>
            </div>
          </FormLabel>

          <div className={styles.claimsTable}>
            <div className={styles.claimsHeader}>
              <div className={styles.claimNameColumn}>Claim Name</div>
              <div className={styles.claimValueColumn}>Claim Value</div>
              <div className={styles.claimWildcardColumn}>Has Wildcards</div>
              <div className={styles.claimActionColumn}>{/* Actions */}</div>
            </div>

            {requiredClaims.map((claim, index) => (
              <div key={claim.id} className={styles.claimRow}>
                <div className={styles.claimNameColumn}>
                  <TextInput
                    label={`Claim ${index + 1} name`}
                    isRequired
                    type="text"
                    placeholder="sub"
                    value={claim.claim}
                    onChange={(value) => handleClaimChange(index, 'claim', value)}
                    onBlur={() => handleClaimBlur(index, 'claim')}
                    isDisabled={index === 0 || createTrustRelationshipMutation.isPending}
                  />
                  {claimErrors[index]?.claim && <span className={styles.error}>{claimErrors[index].claim}</span>}
                </div>
                <div className={styles.claimValueColumn}>
                  <TextInput
                    label={`Claim ${index + 1} value`}
                    isRequired
                    type="text"
                    placeholder="repo:fossas/*"
                    value={claim.value}
                    onChange={(value) => handleClaimChange(index, 'value', value)}
                    onBlur={() => handleClaimBlur(index, 'value')}
                    isDisabled={createTrustRelationshipMutation.isPending}
                  />
                  {claimErrors[index]?.value && <span className={styles.error}>{claimErrors[index].value}</span>}
                </div>
                <div className={styles.claimWildcardColumn}>
                  <Checkbox
                    isChecked={claim.hasWildcards}
                    onChange={(_, checked) => handleClaimChange(index, 'hasWildcards', checked)}
                    isDisabled={createTrustRelationshipMutation.isPending}
                  />
                </div>
                <div className={styles.claimActionColumn}>
                  {index !== 0 && (
                    <IconButton
                      id={`remove-claim-${index}-icon`}
                      icon={icons.times}
                      onClick={() => handleRemoveClaim(index)}
                      isDisabled={createTrustRelationshipMutation.isPending}
                      tooltip="Remove claim"
                      testId={`remove-claim-${index}`}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>

          {requiredClaims.length < 5 && (
            <Button onClick={handleAddClaim} isDisabled={createTrustRelationshipMutation.isPending} type="secondary">
              Add Claim
            </Button>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <ButtonGroup>
          <Button onClick={onHide} isDisabled={createTrustRelationshipMutation.isPending} type="secondary">
            Cancel
          </Button>
          <LoaderButton
            type="primary"
            onClick={handleSubmit}
            isLoading={createTrustRelationshipMutation.isPending}
            isDisabled={isSubmitDisabled}
          >
            Create Trust Relationship
          </LoaderButton>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalCreateTrustRelationship;
